// eTAMS — Travel requests (PDF page 2) + request detail
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { etamsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Stat, Card, Badge, Empty, Loading, Modal, Flow, VFlow, Note, Select, Seg,
  useAsync, useSort, Th, usePaged, Pager, money, pad2, fmtRange, fmtDate, fmtDateTime, downloadCSV,
  useQueryParam, useAction, promptText, confirmAction, FileChip,
} from '../../components/ui';
import { travelStatus, advanceStatus } from '../../utils/etams';

export function TravelDetail({ id, onClose, onChanged }) {
  const navigate = useNavigate();
  const { data, loading, reload } = useAsync(() => (id ? etamsAPI.getTravel(id) : Promise.resolve(null)), [id]);
  const [busy, run] = useAction();
  const t = data?.request;
  const st = travelStatus(t);
  const c = t?.costs || {};

  const cancel = async () => {
    const remarks = await promptText({ title: 'Cancel travel request?', label: 'Reason', confirmText: 'Cancel request', color: '#c43c31' });
    if (remarks === null) return;
    const r = await run('cancel', () => etamsAPI.cancelTravel(t._id, remarks), 'Travel request cancelled');
    if (r) { reload(); onChanged && onChanged(); }
  };
  const submit = async () => {
    const r = await run('submit', () => etamsAPI.submitTravel(t._id));
    if (r) { reload(); onChanged && onChanged(); }
  };
  const remove = async () => {
    if (!(await confirmAction('Delete this draft?', 'This cannot be undone.', 'Delete', '#c43c31'))) return;
    const r = await run('delete', () => etamsAPI.deleteTravel(t._id), 'Draft deleted');
    if (r) { onChanged && onChanged(); onClose(); }
  };

  const history = (t?.approvalHistory || []).map((h) => ({
    state: { APPROVED: 'done', SUBMITTED: 'done', REJECTED: 'rejected', RETURNED: 'returned', CANCELLED: 'rejected' }[h.action],
    title: `${h.action === 'SUBMITTED' ? 'Submitted' : h.action.charAt(0) + h.action.slice(1).toLowerCase()} · ${{ EMPLOYEE: 'Employee', MANAGER: 'Reporting manager', HOD: 'HOD', ADMIN: 'Administration' }[h.stage] || h.stage}`,
    sub: h.remarks, meta: <>{h.approverName || ''}<br />{fmtDateTime(h.actionAt)}</>,
  }));

  return (
    <Modal open={!!id} onClose={onClose} size="lg" title={t ? `Travel request ${t.requestNo || '(draft)'}` : 'Travel request'} sub={t ? `${t.origin || ''} → ${t.destination || ''}` : ''}
      footer={t && (
        <>
          {t.isOwner && ['DRAFT', 'RETURNED'].includes(t.status) && <button type="button" className="tse-btn danger-outline" onClick={remove} disabled={!!busy || t.status !== 'DRAFT'}>Delete draft</button>}
          {t.isOwner && ['PENDING_MANAGER', 'PENDING_HOD', 'PENDING_ADMIN', 'APPROVED'].includes(t.status) && <button type="button" className="tse-btn danger-outline" onClick={cancel} disabled={!!busy}>Cancel request</button>}
          {t.isOwner && ['DRAFT', 'RETURNED'].includes(t.status) && <button type="button" className="tse-btn outline" onClick={() => navigate(`/etams/travel-requests/new?id=${t._id}`)}>Edit</button>}
          {t.isOwner && ['DRAFT', 'RETURNED'].includes(t.status) && <button type="button" className="tse-btn" onClick={submit} disabled={!!busy}>Submit for approval</button>}
          {t.isOwner && t.status === 'APPROVED' && <button type="button" className="tse-btn outline" onClick={() => navigate(`/expense/advance-request?journey=${t._id}`)}>Request an advance</button>}
          {t.isOwner && t.status === 'APPROVED' && <button type="button" className="tse-btn" onClick={() => navigate(`/etams/claims/new?sanction=${t._id}`)}>Create expense claim</button>}
          {t.canAct && <button type="button" className="tse-btn" onClick={() => navigate('/expense/approvals')}>Review in approval inbox</button>}
        </>
      )}>
      {loading || !t ? <Loading /> : (
        <div className="tse-stack" style={{ gap: 8 }}>
          <div className="tse-row"><Badge tone={st.tone} icon={st.icon} lg>{st.label}{st.stage ? ` — ${st.stage}` : ''}</Badge>{t.booking?.reference && <Badge tone="blue" icon="bi-ticket-perforated">Booking {t.booking.reference}</Badge>}</div>
          {t.status === 'RETURNED' && <Note tone="warn" title="Returned for correction">{(t.approvalHistory || []).filter((h) => h.action === 'RETURNED').slice(-1)[0]?.remarks}</Note>}
          {(t.policyFlags || []).length > 0 && <Note tone="warn" title="Policy checks">{t.policyFlags.join(' · ')}</Note>}
          <div className="tse-form-grid c3">
            {[
              ['Employee', `${t.employeeName} (${t.employeeCode || '—'})`], ['Business unit', t.businessUnit || '—'], ['Project', t.projectCode || '—'],
              ['Policy band', t.policyBand || '—'], ['City tier', `Tier ${t.cityTier || 'C'}`], ['Mode of travel', t.modeOfTravel || '—'],
              ['Dates', fmtRange(t.departureDate, t.returnDate)], ['Days', t.totalDays || '—'], ['Purpose', t.purpose || '—'],
            ].map(([k, v]) => <div key={k}><div className="tse-muted tse-small">{k}</div><div className="tse-strong">{v}</div></div>)}
          </div>
          <div className="tse-card" style={{ boxShadow: 'none' }}>
            <div className="tse-table-wrap">
              <table className="tse-table compact">
                <thead><tr><th>Fare</th><th>Hotel</th><th>Food</th><th>OPE</th><th>Conveyance</th><th>Other</th><th className="num">Estimated total</th></tr></thead>
                <tbody><tr>
                  <td>{money(c.fare)}</td><td>{money(c.hotel)}</td><td>{money(c.food)}</td><td>{money(c.ope)}</td><td>{money(c.conveyance)}</td><td>{money(c.other)}</td>
                  <td className="num strong">{money(t.estimatedTotal)}</td>
                </tr></tbody>
              </table>
            </div>
          </div>
          <div className="tse-row tse-small"><span className="tse-muted">Advance ceiling:</span><strong>{money(t.advanceCeiling)}</strong></div>
          {(data.advances || []).length > 0 && (
            <div>
              <div className="tse-label" style={{ marginBottom: 6 }}>Linked advances</div>
              {data.advances.map((a) => { const as = advanceStatus(a); return <div key={a._id} className="tse-row tse-small" style={{ gap: 8 }}><span className="tse-ref">{a.requestId}</span>{money(a.advanceAmountRequested)}<Badge tone={as.tone}>{as.label}</Badge>{a.advanceReleasedAmount > 0 && <span className="tse-muted">released {money(a.advanceReleasedAmount)}</span>}</div>; })}
            </div>
          )}
          {(t.attachments || []).length > 0 && <div className="tse-grid g-2">{t.attachments.map((a) => <FileChip key={a.s3Key} file={a} />)}</div>}
          {history.length > 0 && <div><div className="tse-label" style={{ marginBottom: 10 }}>Approval history</div><VFlow items={history} /></div>}
        </div>
      )}
    </Modal>
  );
}

export default function TravelRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openId = useQueryParam('id');
  const [detail, setDetail] = useState(openId);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('mine');
  const canSeeTeam = ['admin', 'manager', 'hod', 'finance', 'hr'].includes(user?.role);
  const list = useAsync(() => etamsAPI.listTravel({ scope }), [scope]);
  const stats = useAsync(() => etamsAPI.travelStats(), []);
  useEffect(() => { if (openId) setDetail(openId); }, [openId]);
  const reload = () => { list.reload(); stats.reload(); };

  const statusGroup = (s) => (['PENDING_MANAGER', 'PENDING_HOD', 'PENDING_ADMIN'].includes(s) ? 'PENDING' : ['DRAFT', 'RETURNED'].includes(s) ? 'DRAFT' : s);
  const rows = useMemo(() => {
    let r = list.data?.requests || [];
    if (status) r = r.filter((x) => statusGroup(x.status) === status);
    if (q.trim()) {
      const t = q.toLowerCase();
      r = r.filter((x) => [x.requestNo, x.origin, x.destination, x.projectCode, x.purpose, x.employeeName].some((v) => String(v || '').toLowerCase().includes(t)));
    }
    return r;
  }, [list.data, status, q]);
  const sort = useSort(rows, 'createdAt', 'desc');
  const paged = usePaged(sort.rows, 10);
  const s = stats.data?.stats || {};

  const exportCsv = () => downloadCSV('travel-requests.csv', sort.rows, [
    { label: 'Request', value: (r) => r.requestNo || 'Draft' }, { label: 'Employee', value: 'employeeName' },
    { label: 'Origin', value: 'origin' }, { label: 'Destination', value: 'destination' }, { label: 'Project', value: 'projectCode' },
    { label: 'Departure', value: (r) => fmtDate(r.departureDate) }, { label: 'Return', value: (r) => fmtDate(r.returnDate) },
    { label: 'Estimate', value: 'estimatedTotal' }, { label: 'Status', value: (r) => travelStatus(r).label },
  ]);

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title="Travel requests" subtitle="View and track your travel requests"
        actions={<Link to="/etams/travel-requests/new" className="tse-btn lg"><i className="bi bi-plus-lg" />New travel request</Link>} />

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-file-earmark-text" tone="blue" label="All requests" value={pad2(s.all)} tint="blue" loading={stats.loading} onClick={() => setStatus('')} />
        <Stat icon="bi-clock" tone="amber" label="Awaiting approval" value={pad2(s.awaitingApproval)} tint="amber" loading={stats.loading} onClick={() => setStatus('PENDING')} />
        <Stat icon="bi-check-circle-fill" tone="green" label="Approved" value={pad2(s.approved)} tint="green" loading={stats.loading} onClick={() => setStatus('APPROVED')} />
        <Stat icon="bi-file-earmark" tone="gray" label="Drafts" value={pad2(s.drafts)} loading={stats.loading} onClick={() => setStatus('DRAFT')} />
      </div>

      <Card bodyClass="flush" className="mb-18">
        <div className="tse-toolbar">
          <div className="tse-search"><i className="bi bi-search" /><input placeholder="Search requests" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          {canSeeTeam && <div style={{ width: 260 }}><Seg value={scope} onChange={setScope} tabs={[{ key: 'mine', label: 'My requests' }, { key: 'all', label: user?.role === 'admin' ? 'All requests' : 'Team' }]} /></div>}
          <div className="tse-spacer" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 220 }} options={[
            { value: '', label: 'All statuses' }, { value: 'PENDING', label: 'Awaiting approval' }, { value: 'APPROVED', label: 'Approved' },
            { value: 'DRAFT', label: 'Drafts / returned' }, { value: 'REJECTED', label: 'Rejected' }, { value: 'CANCELLED', label: 'Cancelled' },
          ]} />
          <button type="button" className="tse-btn outline" onClick={exportCsv}><i className="bi bi-box-arrow-up" />Export</button>
        </div>
        <div style={{ padding: '0 11px 3px' }}>
          {list.loading ? <Loading /> : !rows.length ? <Empty icon="bi-airplane" title="No travel requests found" action={<Link className="tse-btn sm" to="/etams/travel-requests/new">New travel request</Link>} /> : (
            <div className="tse-table-wrap">
              <table className="tse-table navy bordered">
                <thead>
                  <tr>
                    <Th sort={sort} k="requestNo">Request</Th>
                    <th>Route</th>
                    <th>Project</th>
                    <Th sort={sort} k="departureDate">Dates</Th>
                    <Th sort={sort} k="estimatedTotal">Estimate</Th>
                    <Th sort={sort} k="status">Status</Th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.rows.map((r) => {
                    const st = travelStatus(r);
                    const editable = r.isOwner && ['DRAFT', 'RETURNED'].includes(r.status);
                    return (
                      <tr key={r._id}>
                        <td>{r.requestNo || <span className="tse-muted">Draft</span>}{scope !== 'mine' && <div className="tse-small tse-muted">{r.employeeName}</div>}</td>
                        <td>{r.origin || '—'} → {r.destination || '—'}</td>
                        <td>{r.projectCode || '—'}</td>
                        <td>{fmtRange(r.departureDate, r.returnDate)}</td>
                        <td>{money(r.estimatedTotal)}</td>
                        <td><Badge tone={st.tone} icon={st.icon} lg>{st.label}</Badge>{st.stage && <div className="tse-small tse-muted" style={{ marginTop: 3 }}>{st.stage}</div>}</td>
                        <td>
                          {editable
                            ? <button type="button" className="tse-btn sm" style={{ minWidth: 110 }} onClick={() => navigate(`/etams/travel-requests/new?id=${r._id}`)}>Continue</button>
                            : <button type="button" className="tse-btn sm outline" style={{ minWidth: 110 }} onClick={() => setDetail(r._id)}>View</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Pager {...paged} label="requests" />
      </Card>

      <Card>
        <Flow nodes={[
          { icon: 'bi-file-earmark-text', label: 'Request', state: 'active' },
          { icon: 'bi-person', label: 'Manager & HOD approval' },
          { icon: 'bi-calendar3', label: 'Booking desk' },
          { icon: 'bi-airplane', label: 'Travel' },
        ]} />
      </Card>
      <div className="tse-row tse-small tse-muted" style={{ marginTop: 10 }}>TSE-HR-TAE-001 · Proposed Rev. 01</div>

      <TravelDetail id={detail} onClose={() => { setDetail(null); if (openId) navigate('/etams/travel-requests', { replace: true }); }} onChanged={reload} />
    </PageShell>
  );
}

